import {
  BALANCE_DELTA,
  DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
  EMPTY_OBJECT,
  MixpanelEvent,
  MixpanelEventProperty,
  columnTypes,
  filterByCellValueMenuItem,
  filterByColumnMainMenuItems,
  getAgGridColId,
  isColumn,
  isGridApiReady,
  removeEmptyFilters,
  useAccordionFilterBuilder,
  useAggDeltaUpdatesPipe,
  useCallbackRef,
  useConstant,
  useDefaultColumns,
  useMixpanel,
  usePersistedBlotterTable,
  useWsBlotterTable,
  type AgGridMenuItems,
  type Column,
  type FilterClause,
  type Leaves,
  type WebsocketRequest,
} from '@talos/kyoko';
import { compact, isEqual } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import { v1 as uuid } from 'uuid';

import { useDisplaySettings } from '@src/providers/DisplaySettingsProvider';
import { getBalanceKey, type Balance, type BalancesFilter } from '@src/types';
import type {
  GetContextMenuItemsParams,
  GetMainMenuItemsParams,
  SelectionChangedEvent,
  StateUpdatedEvent,
} from 'ag-grid-community';
import { getBalanceRelatedEntitiesConfig } from './balancesRelatedEntities';
import { defaultByCounterpartyColumns } from './defaults';
import type { BalancesBlotterShowBy, BalancesV2TabProps } from './types';
import { useBalancesColumnDefinitions } from './useBalancesV2Columns';
import { BalanceFilterKey, balanceColIDToFilterBuilderKey, useBalancesV2Filter } from './useBalancesV2Filter';
import { useEnrichDeltaBalancesPipe } from './useEnrichedDeltaBalancesObs';

export const balancesBlotterAggSpecs = [
  { valuePath: 'Equivalent.Amount', currencyPath: 'Equivalent.Currency' },
  { valuePath: 'Equivalent.AvailableAmount', currencyPath: 'Equivalent.Currency' },
  { valuePath: 'Equivalent.OutstandingBuy', currencyPath: 'Equivalent.Currency' },
  { valuePath: 'Equivalent.OutstandingSell', currencyPath: 'Equivalent.Currency' },
] satisfies { valuePath: Leaves<Balance>; currencyPath: Leaves<Balance> }[];

export interface BalancesGroupingState {
  groupAccounts: boolean;
  showBy: BalancesBlotterShowBy;
}

export function getBalancesGroupingState(groupedColIds: Set<string>): BalancesGroupingState {
  return {
    groupAccounts: groupedColIds.has('marketAccountGroup'),
    showBy: groupedColIds.has('Currency') ? 'asset' : 'counterparty',
  };
}

export function getBalancesRowGroupColumns(showBy: BalancesBlotterShowBy): (keyof Balance)[] {
  return showBy === 'asset' ? ['Currency'] : ['marketAccountName'];
}

export function getBalancesGroupedAccountRowGroupColumns(): (keyof Balance)[] {
  return ['marketAccountGroup', 'marketAccountName'];
}

interface UseBalancesV2BlotterFoundationParams {
  blotterID: string;
  tab: BalancesV2TabProps;
  onUpdateTab: (updatedTab: BalancesV2TabProps) => void;
  /** When mounting should the filter accordion be expanded. */
  initialIsOpen?: boolean;
  enableContextMenus?: boolean;
  enableRowSelection?: boolean;
  enableTotals?: boolean;
  groupHideParentOfSingleChild?: boolean;
}

export function useBalancesV2BlotterFoundation({
  blotterID,
  tab,
  onUpdateTab,
  initialIsOpen,
  enableContextMenus = false,
  enableRowSelection = false,
  enableTotals = true,
  groupHideParentOfSingleChild = true,
}: UseBalancesV2BlotterFoundationParams) {
  const mixpanel = useMixpanel();

  const defaultBlotterColumns = tab.defaultColumns ?? defaultByCounterpartyColumns;
  const defaultFilter = tab.defaultFilter ?? EMPTY_OBJECT;
  const showZeroBalances = tab.showZeroBalances ?? false;
  const showTotals = tab.showTotals ?? true;

  const { homeCurrency } = useDisplaySettings();
  const relatedEntities = useMemo(() => getBalanceRelatedEntitiesConfig(), []);
  const updateShowZeroBalances = useCallbackRef((value: boolean) => {
    mixpanel.track(MixpanelEvent.ToggleShowZeroBalances, { [MixpanelEventProperty.Enabled]: value });
    onUpdateTab({ ...tab, showZeroBalances: value });
  });
  const updateShowTotals = useCallbackRef((value: boolean) => {
    mixpanel.track(MixpanelEvent.ToggleShowTotals);
    onUpdateTab({ ...tab, showTotals: value });
  });
  const [selectedRows, setSelectedRows] = useState<Balance[]>([]);

  const columnDefinitions = useBalancesColumnDefinitions();
  const defaultColumns = useDefaultColumns(defaultBlotterColumns, columnDefinitions);

  const persistedBlotterTable = usePersistedBlotterTable(blotterID, { filter: defaultFilter });

  const filtering = useBalancesV2Filter({
    initialFilter: persistedBlotterTable.initialState?.filter,
    saveFilter: persistedBlotterTable.onFilterChanged,
  });

  const { changeFilter, filterableProperties, initialFilterClauses } = filtering;

  const handleFilterClausesChanged = useCallback(
    (filterClausesByPropertyKey: Map<string, FilterClause>) => {
      changeFilter(curr => {
        const newFilter: BalancesFilter = {
          Currencies: filterClausesByPropertyKey.get(BalanceFilterKey.Currencies)?.selections,
          Markets: filterClausesByPropertyKey.get(BalanceFilterKey.Markets)?.selections,
          MarketTypes: filterClausesByPropertyKey.get(BalanceFilterKey.MarketTypes)?.selections,
          Groups: filterClausesByPropertyKey.get(BalanceFilterKey.Groups)?.selections,
          MarketAccounts: filterClausesByPropertyKey.get(BalanceFilterKey.MarketAccounts)?.selections,
        };

        const proposed = removeEmptyFilters({
          ...curr,
          ...newFilter,
        });

        if (isEqual(curr, proposed)) {
          return curr;
        }

        return proposed;
      });
    },
    [changeFilter]
  );

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: initialIsOpen },
    filterBuilderProps: {
      initialFilterClauses,
      properties: filterableProperties,
      onFilterClausesChanged: handleFilterClausesChanged,
    },
  });

  const { openClause } = filterBuilderAccordion;

  const columnsOnly = useMemo(() => defaultColumns.filter(isColumn), [defaultColumns]);
  const initialRowGroupColumnIds = useMemo(
    () =>
      getRowGroupColumnIdsFromState(persistedBlotterTable.initialState?.gridState) ??
      getRowGroupColumnIdsFromColumns(columnsOnly),
    [columnsOnly, persistedBlotterTable.initialState?.gridState]
  );
  const initialGroupingState = useMemo(
    () => getBalancesGroupingState(initialRowGroupColumnIds),
    [initialRowGroupColumnIds]
  );
  const [groupAccounts, setGroupAccounts] = useState<boolean>(initialGroupingState.groupAccounts);
  const [showBy, setShowBy] = useState<BalancesBlotterShowBy>(initialGroupingState.showBy);

  const handleGridStateUpdated = useCallbackRef((event: StateUpdatedEvent) => {
    const groupedColIds = getRowGroupColumnIdsFromState(event.state);
    if (groupedColIds != null) {
      const groupingState = getBalancesGroupingState(groupedColIds);
      setShowBy(groupingState.showBy);
      setGroupAccounts(groupingState.groupAccounts);
    }
  });

  const getExtraMainMenuItems = useCallbackRef((params: GetMainMenuItemsParams) => {
    return filterByColumnMainMenuItems({
      params,
      colIDToFilterBuilderKey: balanceColIDToFilterBuilderKey,
      openClause,
      mixpanel,
    });
  });

  const getCustomContextMenuItems = useCallbackRef(
    (params: GetContextMenuItemsParams<Balance>): AgGridMenuItems =>
      filterByCellValueMenuItem({
        params,
        filterableProperties,
        openClause,
        colIDToFilterBuilderKey: balanceColIDToFilterBuilderKey,
        mixpanel,
      })
  );

  const handleRowSelectionChanged = useCallback(({ api }: SelectionChangedEvent<Balance>) => {
    if (!isGridApiReady(api)) {
      return;
    }
    const rows = api.getSelectedRows();
    return setSelectedRows(compact(rows));
  }, []);

  const blotterTag = useConstant(`BALANCES_V2_${uuid()}`);

  const pipe = useEnrichDeltaBalancesPipe();

  const aggPipe = useAggDeltaUpdatesPipe({
    getUniqueKey: getBalanceKey,
    aggSpecs: balancesBlotterAggSpecs,
  });

  const autoGroupColumnDef = useMemo(
    () =>
      columnTypes.group({
        type: 'group',
        title: 'Grouping',
        editable: false,
        hide: false,
        suppressColumnsToolPanel: false,
        params: {
          suppressCount: true,
        },
      }),
    []
  );

  const request = useMemo(
    () => ({
      name: BALANCE_DELTA,
      tag: blotterTag,
      EquivalentCurrency: homeCurrency,
      ShowZeroBalances: showZeroBalances,
      ...removeEmptyFilters(filtering.filter),
    }),
    [blotterTag, homeCurrency, showZeroBalances, filtering.filter]
  );
  const blotterTable = useWsBlotterTable<WebsocketRequest, Balance>({
    request,
    columns: defaultColumns,
    pipe,
    pinnedRowDataPipe: enableTotals ? aggPipe : undefined,
    showPinnedRows: enableTotals && showTotals,
    rowID: 'rowID' satisfies keyof Balance,

    getExtraMainMenuItems: enableContextMenus ? getExtraMainMenuItems : undefined,
    persistence: persistedBlotterTable,
    contextMenuOptions: enableContextMenus ? { getCustomContextMenuItems, relatedEntities } : undefined,
    gridOptions: {
      onStateUpdated: handleGridStateUpdated,
      groupHideParentOfSingleChild,
      autoGroupColumnDef,
      groupDisplayType: 'singleColumn',
      ...(enableRowSelection
        ? {
            rowSelection: DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
            onSelectionChanged: handleRowSelectionChanged,
          }
        : undefined),
    },
    exportOptions: {
      name: 'Balances',
      tabLabel: tab.label,
    },
  });

  const { setRowGroupColumns, removeRowGroupColumns } = blotterTable;

  const handleShowByChanged = useCallbackRef((showBy: BalancesBlotterShowBy) => {
    setRowGroupColumns(getBalancesRowGroupColumns(showBy));
  });

  const handleGroupAccountsToggle = useCallbackRef((checked: boolean) => {
    mixpanel.track(MixpanelEvent.GroupAccounts, { [MixpanelEventProperty.Enabled]: checked });
    if (checked) {
      setRowGroupColumns(getBalancesGroupedAccountRowGroupColumns());
    } else {
      removeRowGroupColumns(['marketAccountGroup'] satisfies (keyof Balance)[]);
    }
  });

  return {
    blotterTable,
    filterBuilderAccordion,
    groupAccounts,
    handleGroupAccountsToggle,
    handleShowByChanged,
    selectedRows,
    showBy,
    showTotals,
    showZeroBalances,
    updateShowTotals,
    updateShowZeroBalances,
  };
}

function getRowGroupColumnIdsFromColumns(columns: Column[]): Set<string> {
  return new Set(columns.filter(column => column.rowGroup).map(getAgGridColId));
}

function getRowGroupColumnIdsFromState(state: StateUpdatedEvent['state'] | undefined): Set<string> | undefined {
  return state?.rowGroup?.groupColIds ? new Set(state.rowGroup.groupColIds) : undefined;
}
